import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { LINKS, SERVICES, TESTIMONIALS } from "../lib/config";
import CalendlyModal from "../components/CalendlyModal";
import Nav from "../components/Nav";

// Content for each phase
const PHASES = {
    gravidanza: {
        title: "Allenamento in Gravidanza",
        tagline: "Un percorso sicuro, adatto al tuo trimestre.",
        introParts: [
            "Raccontami a che mese sei, come stai e che obiettivo hai.",
            "Costruiremo insieme un percorso sicuro e adatto al tuo trimestre, che ti accompagni fino al parto e che si adatti alle tue esigenze.",
        ],
        longParts: [
            "Contrariamente a quanto si pensa, allenarsi in gravidanza non è pericoloso: fatto con criterio è protettivo.",
            "Con il metodo giusto puoi mantenere tono muscolare, ridurre il rischio di mal di schiena e diastasi, migliorare respirazione e recupero post-parto.",
            "Ogni esercizio viene adattato in base al tuo trimestre, al tuo livello di partenza e ai tuoi feedback.",
        ],
        forWho: [
            "Chi è in gravidanza e vuole allenarsi in sicurezza",
            "Chi non si è mai allenata ma vuole iniziare adesso",
            "Chi ha già una routine e vuole adattarla al pancione",
            "Chi cerca alleviare mal di schiena, tensioni e affaticamento",
        ],
    },
    "post-parto": {
        title: "Allenamento Post Gravidanza",
        tagline: "Ricostruiamo il tuo corpo con i tuoi tempi.",
        introParts: [
            "Il tuo corpo ha appena creato una vita.",
            "Insieme lo ricostruiamo con cura e con i tuoi tempi: dal recupero del pavimento pelvico al ritorno alla forma, con obiettivi realistici e allenamenti pensati per la neo-mamma.",
        ],
        longParts: [
            "Il ritorno al fitness dopo il parto non è una gara. È un percorso graduale che rispetta il corpo della mamma e la nuova quotidianità con il tuo bambino.",
            "Iniziamo con lavoro dolce e progressivo, per poi arrivare — nei tempi giusti — a un corpo più forte, energico e ritrovato di prima.",
        ],
        forWho: [
            "Chi ha appena partorito e vuole ricominciare in sicurezza",
            "Chi convive con diastasi o incontinenza post parto",
            "Chi vuole tornare alla forma pre-gravidanza con un metodo strutturato",
            "Chi cerca allenamenti brevi ma efficaci, gestibili tra una poppata e l'altra",
        ],
    },
};

const PACKAGES_BY_PHASE = {
    gravidanza: [
        {
            id: "trimestre",
            name: "Trimestre",
            durationLabel: "12 settimane",
            badge: null,
            subtitle: "Un trimestre di allenamento in sicurezza",
            price: "€ 290",
            features: [
                "Primo incontro conoscitivo",
                "2 piani di allenamento personalizzati (aggiornati ogni 6 settimane)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "percorso-mamma",
            name: "Percorso mamma",
            durationLabel: "24 settimane",
            badge: "Più scelto",
            scarcity: "Solo 5 posti nuovi al mese",
            subtitle: "Ti accompagno per due trimestri",
            price: "€ 490",
            features: [
                "Primo incontro conoscitivo",
                "4 piani di allenamento personalizzati (aggiornati ogni 6 settimane)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Consulenza di monitoraggio a metà percorso",
                "Correzioni video",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "gravidanza-completa",
            name: "Gravidanza completa",
            durationLabel: "Percorso completo",
            badge: null,
            subtitle: "Ti seguo dall'inizio fino al momento del parto",
            price: "€ 590",
            features: [
                "Primo incontro conoscitivo",
                "6 piani di allenamento personalizzati (aggiornati ogni 6 settimane)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Consulenze di monitoraggio periodiche",
                "Correzioni video",
                "Programmazione ad hoc per il parto",
                "Supporto WhatsApp",
            ],
        },
    ],
    "post-parto": [
        {
            id: "riparti",
            name: "Riparti",
            durationLabel: "6 settimane",
            badge: null,
            subtitle: "Per ricominciare in sicurezza",
            price: "€ 150",
            features: [
                "Primo incontro conoscitivo",
                "Piano di allenamento personalizzato per il post parto",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "ricostruisci",
            name: "Ricostruisci",
            durationLabel: "12 settimane",
            badge: "Più scelto",
            scarcity: "Solo 5 posti nuovi al mese",
            subtitle: "Ricostruzione graduale del tuo corpo",
            price: "€ 270",
            features: [
                "Primo incontro conoscitivo",
                "Piani di allenamento personalizzati (aggiornati a metà percorso)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "Consulenza di monitoraggio a metà percorso",
                "Correzioni video",
                "Supporto WhatsApp",
            ],
        },
        {
            id: "torna-forte",
            name: "Torna forte",
            durationLabel: "18 settimane",
            badge: null,
            subtitle: "Per un recupero completo",
            price: "€ 380",
            features: [
                "Primo incontro conoscitivo",
                "Piani di allenamento personalizzati (aggiornati ogni 6 settimane)",
                "Accesso all'app dedicata",
                "Video tutorial degli esercizi",
                "2 consulenze di monitoraggio",
                "Correzioni video",
                "Supporto WhatsApp",
            ],
        },
    ],
};

export default function PhasePage() {
    const { slug } = useParams();
    const [calOpen, setCalOpen] = useState(false);
    const onBook = () => setCalOpen(true);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    const phase = PHASES[slug];
    const service = SERVICES.find((s) => s.category === (slug === "gravidanza" ? "gravidanza" : "post-parto"));
    const packages = PACKAGES_BY_PHASE[slug] || [];
    const phaseTestimonial = TESTIMONIALS.find((t) => t.category === "Gravidanza");

    if (!phase) {
        return (
            <div className="min-h-screen bg-powder flex flex-col items-center justify-center gap-6 px-5 text-center">
                <h1 className="font-serif text-4xl">Pagina non trovata</h1>
                <Link to="/percorso/fitmama" className="btn-fuchsia">Torna a FitMama</Link>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-powder text-ink" data-testid={`phase-page-${slug}`}>
            <div className="grain-overlay" />
            <Nav onBook={onBook} variant="solid" theme="fuchsia" />

            {/* Hero */}
            <section className="relative min-h-[55vh] md:min-h-[65vh] flex items-end overflow-hidden bg-ink">
                <motion.img
                    initial={{ scale: 1.12 }} animate={{ scale: 1 }}
                    transition={{ duration: 1.5, ease: [0.65, 0, 0.35, 1] }}
                    src={service?.img} alt={phase.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
                <div className="relative z-10 px-5 md:px-12 pb-10 md:pb-14 max-w-[1400px] mx-auto w-full text-white">
                    <Link
                        to="/percorso/fitmama"
                        data-testid="phase-back-link"
                        className="editorial-link text-white/70 text-sm inline-flex items-center gap-1 mb-4"
                    >
                        ← FitMama
                    </Link>
                    <h1 className="font-serif text-5xl md:text-7xl font-bold leading-[0.95]">{phase.title}</h1>
                    <p className="font-serif italic text-xl md:text-2xl mt-4 text-white/90">{phase.tagline}</p>
                </div>
            </section>

            {/* Intro */}
            <section className="max-w-3xl mx-auto px-5 md:px-8 py-16 md:py-24 text-center">
                {phase.introParts.map((line, idx) => (
                    <motion.p
                        key={`intro-${idx}`}
                        initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: idx * 0.1 }}
                        className="font-serif text-2xl md:text-3xl leading-snug mb-6"
                    >
                        {line}
                    </motion.p>
                ))}

                <div className="mb-10 mt-10 max-w-2xl mx-auto space-y-5">
                    {phase.longParts.map((line, idx) => (
                        <motion.p
                            key={`long-${idx}`}
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.7, delay: idx * 0.1 }}
                            className="font-sans text-base md:text-lg text-ink-muted leading-relaxed"
                        >
                            {line}
                        </motion.p>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="mb-14"
                >
                    <button
                        onClick={onBook}
                        data-testid="phase-book-btn-intro"
                        className="btn-ghost"
                    >
                        Prenota la tua consulenza gratuita <ArrowUpRight size={16} />
                    </button>
                </motion.div>

                <h2 className="font-serif text-3xl md:text-4xl mb-6 text-left">A chi è rivolto</h2>
                <ul className="space-y-4 mb-4 text-left">
                    {phase.forWho.map((item, i) => (
                        <li key={i} className="flex gap-4 items-start border-b border-ink/10 pb-4">
                            <span className="mt-1 w-6 h-6 rounded-full bg-fuchsia text-white flex items-center justify-center shrink-0">
                                <Check size={14} />
                            </span>
                            <span className="font-sans text-base md:text-lg">{item}</span>
                        </li>
                    ))}
                </ul>
            </section>

            {/* Struttura del percorso */}
            <section className="relative bg-powder-darker py-20 md:py-28">
                <div className="max-w-3xl mx-auto px-5 md:px-8">
                    <div className="bg-fuchsia text-white p-7 md:p-9">
                        <p className="text-xs uppercase tracking-[0.28em] font-sans text-white/80 mb-6">
                            Struttura del percorso
                        </p>
                        <ul className="space-y-4">
                            {[
                                "Primo incontro conoscitivo",
                                "Creazione del programma su misura e accesso all'app",
                                "Correzioni video ed eventuali modifiche in corso d'opera",
                                "Attività di monitoring e follow-up",
                            ].map((b, i) => (
                                <li key={i} className="flex items-start gap-3">
                                    <span className="mt-1 w-5 h-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                                        <Check size={12} />
                                    </span>
                                    <span className="font-sans text-sm md:text-base">{b}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* Pacchetti e Costi */}
            <section className="relative bg-white py-24 md:py-32" id="pacchetti">
                <div className="max-w-[1400px] mx-auto px-5 md:px-12">
                    {/* Small testimonial */}
                    {phaseTestimonial && (
                        <motion.figure
                            initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                            transition={{ duration: 0.7 }}
                            className="max-w-2xl mx-auto mb-16 md:mb-20 text-center"
                            data-testid={`phase-testimonial-${slug}`}
                        >
                            <p className="text-[10px] uppercase tracking-[0.28em] font-sans text-fuchsia mb-4">
                                Dicono di me
                            </p>
                            <blockquote className="font-serif italic text-xl md:text-2xl leading-snug text-ink mb-3">
                                &ldquo;{phaseTestimonial.quote}&rdquo;
                            </blockquote>
                            <figcaption className="font-sans text-sm text-ink-muted">
                                — {phaseTestimonial.name} · {phaseTestimonial.role}
                            </figcaption>
                        </motion.figure>
                    )}

                    <div className="max-w-3xl mb-14 md:mb-20">
                        <p className="text-xs uppercase tracking-[0.28em] font-sans text-ink-muted mb-4">
                            <span className="inline-block w-8 h-px bg-fuchsia align-middle mr-3" />
                            Pacchetti e costi
                        </p>
                        <h2 className="font-serif text-4xl md:text-6xl leading-[0.95] font-bold text-ink mb-6">
                            Scegli il tuo <span className="italic text-fuchsia">pacchetto</span>.
                        </h2>
                        <p className="font-sans text-base md:text-lg text-ink-muted max-w-xl">
                            Tre percorsi con durata e supporto crescenti. Più a lungo ti alleni con me, più il percorso si adatta e cresce con te.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-5 md:gap-6 items-stretch">
                        {packages.map((p, i) => {
                            const featured = p.badge !== null;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.6, delay: i * 0.1 }}
                                    className={`relative flex flex-col text-center bg-powder p-6 md:p-8 border transition-colors duration-500 ${
                                        featured
                                            ? "border-fuchsia border-2 shadow-2xl md:-mt-4"
                                            : "border-ink/10"
                                    }`}
                                    data-testid={`package-${p.id}`}
                                >
                                    {featured && (
                                        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-fuchsia text-white text-[10px] uppercase tracking-[0.28em] px-3 py-1 rounded-full whitespace-nowrap">
                                            ⭐ {p.badge}
                                        </span>
                                    )}

                                    <div className="mb-6">
                                        <span className="text-[10px] uppercase tracking-[0.28em] text-ink-muted block mb-2">
                                            {p.durationLabel}
                                        </span>
                                        <h3 className="font-serif text-3xl md:text-4xl leading-none text-ink">{p.name}</h3>
                                        <p className="font-sans text-sm text-ink-muted mt-2">{p.subtitle}</p>
                                    </div>

                                    <div className="mb-6 pb-6 border-b border-ink/10">
                                        {p.scarcity && (
                                            <p className="text-[11px] uppercase tracking-[0.24em] font-sans font-medium text-fuchsia mb-3">
                                                {p.scarcity}
                                            </p>
                                        )}
                                        <span className="font-serif text-4xl md:text-5xl font-bold text-fuchsia">{p.price}</span>
                                        <span className="font-sans text-sm text-ink-muted ml-2">/ percorso</span>
                                    </div>

                                    <ul className="space-y-3 mb-8 flex-1 text-left">
                                        {p.features.map((f, j) => (
                                            <li key={j} className="flex items-start gap-3">
                                                <span className="mt-0.5 w-5 h-5 rounded-full bg-fuchsia text-white flex items-center justify-center shrink-0">
                                                    <Check size={12} />
                                                </span>
                                                <span className="font-sans text-sm text-ink">{f}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <a
                                        href={LINKS.whatsappStart}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-testid={`package-cta-${p.id}`}
                                        className={`btn-fuchsia justify-center !py-3 text-sm ${featured ? "" : "!bg-ink hover:!bg-ink/80"}`}
                                    >
                                        Prenota ora il tuo pacchetto <ArrowUpRight size={16} />
                                    </a>
                                </motion.div>
                            );
                        })}
                    </div>

                    {/* CTAs */}
                    <div className="text-center mt-14 md:mt-20 flex flex-col items-center gap-4">
                        <p className="font-sans text-sm text-ink-muted">Hai dubbi o domande?</p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <a
                                href={LINKS.whatsapp}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="phase-doubts-link"
                                className="btn-fuchsia !py-2.5 !px-5 text-sm"
                            >
                                Clicca qui per richiedere info <ArrowUpRight size={16} />
                            </a>
                            <button
                                onClick={onBook}
                                data-testid="phase-book-btn"
                                className="btn-ghost !py-2.5 !px-5 text-sm"
                            >
                                Prenota la tua consulenza gratuita <ArrowUpRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <CalendlyModal open={calOpen} onOpenChange={setCalOpen} />
        </div>
    );
}
